/**
 * blog-slider.js
 * Custom element for the Blog Slider section.
 * Reuses the continuous-scroll logic from home-timeline.js.
 */

class BlogSlider extends BaseComponent {
  init() {
    this.isMobile = window.matchMedia('(max-width: 749px)').matches;

    // Read settings from data attributes
    this.carouselHeight = parseInt(this.dataset.carouselHeight, 10) || 500;
    this.adaptiveHeight = this.dataset.adaptiveHeight === 'true';
    // When there are fewer slides than fit the container, Swiper centres them
    // by default. Sections can opt out with data-center-insufficient="false"
    // so slides stay flush to the left edge of their column.
    this.centerInsufficient = this.dataset.centerInsufficient !== 'false';
    // Opt in with data-mousewheel="true" to let a trackpad swipe or a
    // horizontal wheel scrub the carousel. forceToAxis means only sideways
    // gestures are captured, so vertical page scrolling is unaffected, and
    // releaseOnEdges hands scrolling back to the page at either end.
    this.mousewheelEnabled = this.dataset.mousewheel === 'true';

    // State
    this.swiper = null;
    this.uiObserver = null;

    this.initSlider();

    // Listen for resize
    this._onResize = this._handleResize.bind(this);
    window.addEventListener('resize', this._onResize);

    // Initial height calculation
    this.updateUIMetrics();

    // Start ResizeObserver for robust height tracking
    this.initUIObserver();
  }

  initUIObserver() {
    if (this.uiObserver) this.uiObserver.disconnect();

    this.uiObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => this.updateUIMetrics());
    });

    this.uiObserver.observe(document.documentElement);
  }

  updateUIMetrics() {
    if (!this.isConnected) return;

    // Refresh mobile status
    this.isMobile = window.matchMedia('(max-width: 749px)').matches;

    // Available height calculation
    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
    const footerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--footer-height'), 10) || 0;

    // We also account for the section's top/bottom margins/padding and the title height
    const title = this.querySelector('.blog-slider__title');
    const titleHeight = title ? title.offsetHeight + parseFloat(getComputedStyle(title).marginBottom) : 0;
    const pagination = this.querySelector('.blog-slider__pagination-wrapper');
    const paginationHeight = pagination ? pagination.offsetHeight + parseFloat(getComputedStyle(pagination).marginTop) : 0;

    const availableHeight = window.innerHeight - headerHeight - footerHeight - titleHeight - paginationHeight - 80; // 80px buffer for safety
    this.style.setProperty('--slider-available-height', `${Math.max(300, availableHeight)}px`);

    const swiperEl = this.querySelector('.blog-slider');
    if (swiperEl) {
      if (this.isMobile) {
        swiperEl.style.height = 'auto';
        swiperEl.style.setProperty('--card-height', 'auto');
      } else if (this.adaptiveHeight) {
        swiperEl.style.setProperty('--card-height', 'var(--slider-available-height)');
        swiperEl.style.height = 'var(--slider-available-height)';
      } else {
        swiperEl.style.setProperty('--card-height', `${this.carouselHeight}px`);
        swiperEl.style.height = `${this.carouselHeight}px`;
      }
    }

    if (this.swiper) {
      const wasAutoHeight = this.swiper.params.autoHeight;
      this.swiper.params.autoHeight = this.isMobile;

      this.swiper.update();
      if (this.isMobile) {
        this.swiper.updateAutoHeight();
      }

      // Force a second update to catch dynamic layout shifts
      setTimeout(() => {
        if (this.swiper) {
          this.swiper.update();
          if (this.isMobile) this.swiper.updateAutoHeight();
        }
      }, 100);
    }
  }

  initSlider() {
    const swiperEl = this.querySelector('.blog-slider');
    if (!swiperEl) return;

    const slides = swiperEl.querySelectorAll('.swiper-slide');
    if (slides.length === 0) return;

    const swiperConfig = {
      initialSlide: 0,
      resistance: true,
      resistanceRatio: 0,
      grabCursor: true,
      loop: false,
      speed: 600,
      centerInsufficientSlides: this.centerInsufficient,
      watchOverflow: true,
      mousewheel: this.mousewheelEnabled
        ? {
            forceToAxis: true,
            releaseOnEdges: true,
            thresholdDelta: 4,
          }
        : false,
      breakpoints: {
        // Desktop
        750: {
          slidesPerView: 'auto',
          spaceBetween: 12,
          centeredSlides: false,
          centeredSlidesBounds: true,
          freeMode: {
            enabled: true,
            momentum: true,
            momentumRatio: 0.6,
            momentumBounce: false,
          }
        },
        // Mobile
        0: {
          slidesPerView: 1,
          spaceBetween: 0,
          centeredSlides: true,
          freeMode: false,
          autoHeight: true
        }
      },
      on: {
        slideChange: () => {
          this.updatePagination(this.swiper.activeIndex);
        }
      }
    };

    // Navigation and Pagination elements
    const paginationWrapper = this.querySelector('.blog-slider__pagination-wrapper');
    if (paginationWrapper) {
      this.dots = paginationWrapper.querySelectorAll('.dot-pagination__dot');
      const prevBtn = paginationWrapper.querySelector('.dot-pagination__arrow--prev');
      const nextBtn = paginationWrapper.querySelector('.dot-pagination__arrow--next');

      if (prevBtn && nextBtn) {
        swiperConfig.navigation = {
          prevEl: prevBtn,
          nextEl: nextBtn,
        };
      }
    }

    this.swiper = new Swiper(swiperEl, swiperConfig);

    // Initial dot state
    this.updatePagination(this.swiper.activeIndex);

    // Click events for dots
    if (this.dots) {
      this.dots.forEach((dot, idx) => {
        dot.addEventListener('click', () => {
          this.swiper.slideTo(idx);
        });
      });
    }
  }

  updatePagination(index) {
    if (!this.dots) return;
    this.dots.forEach((dot, idx) => {
      dot.classList.toggle('is-active', idx === index);
    });
  }

  _handleResize() {
    this.isMobile = window.matchMedia('(max-width: 749px)').matches;
    this.updateUIMetrics();
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
    if (this.uiObserver) {
      this.uiObserver.disconnect();
    }
    window.removeEventListener('resize', this._onResize);
  }
}

customElements.define('blog-slider', BlogSlider);
